import { useCallback, useMemo } from 'react';

import type { CapitalizationStyle } from '@aline/common';
import { CAPITALIZATION_LABELS, CAPITALIZATION_STYLES } from '@aline/common';
import { DECLARATIVE_MENU_SEPARATOR } from 'src/components/declarative-menu';
import type { DeclarativeMenuConfig } from 'src/components/declarative-menu';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor } from 'src/layout-engine';
import { pushNotification } from 'src/notifications';
import { useCurrentEditingMode } from 'src/store/document/selectors';

import { useIfEditableMixin } from './menu-mixins';

export function useCapitalizationMenu(): DeclarativeMenuConfig {
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const currentUserId = useCurrentUserId();
  const editingMode = useCurrentEditingMode();
  const editor = useEditor();
  const ifEditable = useIfEditableMixin();
  const activeCapitalizationStyle = useEditorTabStateSelector(
    state => state.derived.activeCapitalizationStyle
  );
  const smallCaps = useEditorTabStateSelector(state => state.derived.fontProperties.smallCaps);

  const applyStyle = useCallback(
    (style: CapitalizationStyle) => () => {
      if (!currentUserId || !editingMode) return;
      const reason = AuthorizedTransforms.applyCapitalizationStyle(editor, {
        authorId: currentUserId,
        editingMode,
        style,
      });
      if (reason)
        pushNotification({
          message: reason,
          variant: 'warning',
        });
      ClientTransforms.focus(editor);
    },
    [AuthorizedTransforms, currentUserId, editingMode, editor]
  );

  const toggleSmallCaps = useCallback(() => {
    if (!currentUserId || !editingMode) return;
    AuthorizedTransforms.toggleTextFormatting(editor, {
      authorId: currentUserId,
      editingMode,
      formatKey: 'smallCaps',
    });
    ClientTransforms.focus(editor);
  }, [AuthorizedTransforms, currentUserId, editingMode, editor]);

  return useMemo(
    () => [
      ...CAPITALIZATION_STYLES.map(style => ({
        checked: activeCapitalizationStyle === style,
        label: CAPITALIZATION_LABELS[style],
        onClick: applyStyle(style),
        ...ifEditable,
      })),
      DECLARATIVE_MENU_SEPARATOR,
      {
        checked: smallCaps,
        label: 'Small Caps',
        onClick: toggleSmallCaps,
        ...ifEditable,
      },
    ],
    [activeCapitalizationStyle, applyStyle, ifEditable, smallCaps, toggleSmallCaps]
  );
}
